import {useReposAppPayload} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {useUrlCreator} from '@github-ui/code-view-shared/hooks/use-url-creator'
import {copyText} from '@github-ui/copy'
import {ssrSafeDocument, ssrSafeWindow} from '@github-ui/ssr-utils'
import {CommandKeybindingHint} from '@github-ui/ui-commands'
import {useSearchParams} from '@github-ui/use-navigate'
import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, registerPortalRoot} from '@primer/react'
import {clsx} from 'clsx'
import React, {useEffect, useEffectEvent, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react'
import {createPortal} from 'react-dom'

import {useDeferredMetadata} from '../../../contexts/DeferredMetadataContext'
import {
  calculatePosition,
  calculateTextContentFromElement,
  positionerID,
  useHighlightedLineMenuOptions,
} from '../../../hooks/use-highlighted-line-menu-options'
import type {SetStickyLinesType} from '../../../hooks/use-sticky-lines'
import {forceAnnouncementToScreenReaders, useIsLineCollapsed} from '../../../utilities/lines'
import type {CodeLineData} from './Code/hooks/use-code-lines'
import styles from './HighlightedLineMenu.module.css'

const portalRootID = 'highlighted-line-menu-container'
export const CODE_BLOB_OVERLAY_PORTAL_ROOT = 'codeBlobOverlayPortalRoot'
export const firstOptionId = 'highlighted-line-menu-first-option'

export function HighlightedLineMenuContainer({children}: React.PropsWithChildren) {
  const overlayPortalRootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (overlayPortalRootRef.current) {
      registerPortalRoot(overlayPortalRootRef.current, CODE_BLOB_OVERLAY_PORTAL_ROOT)
    }
  }, [])

  return (
    <div id={positionerID} className="position-relative">
      {children}
      <div id={portalRootID} />
      <div ref={overlayPortalRootRef} className={styles.overlayPortalRoot} />
    </div>
  )
}

interface HighlightedLineMenuProps {
  codeLineClassName?: string
  offset?: {x: number; y: number}
  lineData?: CodeLineData | null
  onLineStickOrUnstick?: SetStickyLinesType
  onMenuClose?: (value: boolean, otherValue: boolean) => void
  onCollapseToggle?: () => void
  openOnLoad?: boolean
  cursorRef?: React.RefObject<HTMLDivElement | null>
  rowBeginId: string
  rowBeginNumber: number
  rowEndId: string
  rowEndNumber: number
  ref?: React.Ref<HighlightedLineMenuHandle>
}

export interface HighlightedLineMenuHandle {
  setAnchor(anchor: HTMLElement | null): void
}

const HighlightedLineMenu = React.memo(function HighlightedLineMenuWithRef({
  codeLineClassName,
  offset,
  lineData,
  onLineStickOrUnstick,
  onMenuClose,
  onCollapseToggle,
  openOnLoad = false,
  cursorRef,
  rowBeginId,
  rowBeginNumber,
  rowEndId,
  rowEndNumber,
  ref,
}: HighlightedLineMenuProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  useImperativeHandle(ref, () => ({setAnchor}))
  const actionMenuRef = React.useRef<HTMLLIElement>(null)
  const {githubDevUrl} = useReposAppPayload()

  //make the button itself invisible when the menu is being opened up next to the cursor
  //also hide the button it is being repositioned so that it doesn't flash in the corner
  const [visibility, setVisibility] = useState(openOnLoad ? 'hidden' : 'visible')

  const [position, setPosition] = useState<React.CSSProperties | undefined>(undefined)

  // We need to wait for the virtualization render in order to know where this position should be
  // due to possible word wrapping
  useLayoutEffect(() => {
    const changePosition = () => {
      setVisibility('hidden')
      requestAnimationFrame(() => {
        setPosition(calculatePosition(anchor, offset))
        setVisibility('visible')
      })
    }
    changePosition()

    // eslint-disable-next-line github/prefer-observers
    ssrSafeWindow?.addEventListener('resize', changePosition)

    return () => {
      ssrSafeWindow?.removeEventListener('resize', changePosition)
    }
  }, [anchor, offset])

  const [open, setOpen] = React.useState(false)

  const handleOpenOnLoad = useEffectEvent(() => {
    if (openOnLoad) {
      if (cursorRef) setAnchor(cursorRef.current)
      const timeout = window.setTimeout(() => {
        setOpen(true)
        //50ms for the timeout because that gives the menu enough time to render and reposition itself so the
        //menu doesn't open up in the corner
      }, 50)

      return () => {
        window.clearTimeout(timeout)
      }
    }
  })
  useLayoutEffect(() => {
    return handleOpenOnLoad()
  }, [])

  const useEndLineNumber = rowBeginId !== rowEndId

  const {newDiscussionPath, newIssuePath} = useDeferredMetadata()

  const {createPermalink, getUrl} = useUrlCreator()
  const {setShouldBeOpen, expandOrCollapseSection, openUpRefSelector} = useHighlightedLineMenuOptions({
    lineData,
    onLineStickOrUnstick,
    onMenuClose,
    onCollapseToggle,
    setOpen,
  })

  const [urlParams] = useSearchParams()
  const isPlain = urlParams.get('plain') === '1'

  // Reactive read of the authoritative collapse state for the owned section's start line so the
  // Expand/Collapse label updates when the section is toggled.
  const ownedSectionCollapsed = useIsLineCollapsed(lineData?.ownedSection?.startLine ?? -1)

  const linkHash = `L${rowBeginNumber}${useEndLineNumber ? `-L${rowEndNumber}` : ''}`

  // Derive hash from props rather than window.location.hash to avoid stale permalinks
  // when React.memo skips re-renders during the observable update cycle
  const permalinkUrl = createPermalink({absolute: true, params: isPlain ? 'plain=1' : undefined, hash: linkHash})
  const encodedPermalinkUrl = encodeURIComponent(permalinkUrl)

  const element =
    position === undefined ? null : (
      <ActionMenu open={open} onOpenChange={setShouldBeOpen}>
        <ActionMenu.Anchor>
          <IconButton
            className={clsx(codeLineClassName, styles.highlightedLineMenuButton)}
            size="small"
            icon={KebabHorizontalIcon}
            aria-label={`Line ${rowBeginNumber} options`}
            data-testid="highlighted-line-menu-button"
            style={{
              visibility: visibility as React.CSSProperties['visibility'],
              ...position,
            }}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay width="small">
          <ActionList data-testid="highlighted-line-menu">
            {rowBeginNumber === rowEndNumber && (
              <ActionList.Item
                onClick={() => {
                  forceAnnouncementToScreenReaders(`Copied line ${rowBeginNumber}.`)
                  const text = calculateTextContentFromElement(
                    ssrSafeDocument?.getElementById(`LC${rowBeginNumber}`) ?? null,
                  )
                  if (text) copyText(text)
                  setShouldBeOpen(false)
                }}
                onSelect={() => {
                  forceAnnouncementToScreenReaders(`Copied line ${rowBeginNumber}.`)
                  const text = calculateTextContentFromElement(
                    ssrSafeDocument?.getElementById(`LC${rowBeginNumber}`) ?? null,
                  )
                  if (text) copyText(text)
                  setShouldBeOpen(false)
                }}
                ref={actionMenuRef}
                className={firstOptionId}
              >
                Copy line
              </ActionList.Item>
            )}
            {rowBeginNumber !== rowEndNumber && (
              <ActionList.Item
                onClick={() => {
                  forceAnnouncementToScreenReaders(`Copied lines ${rowBeginNumber}-${rowEndNumber}.`)
                  let text = ''
                  for (let i = rowBeginNumber; i <= rowEndNumber; i++) {
                    text += `${calculateTextContentFromElement(ssrSafeDocument?.getElementById(`LC${i}`) ?? null)}${
                      i !== rowEndNumber ? '\n' : ''
                    }`
                  }
                  if (text) copyText(text)
                  setShouldBeOpen(false)
                }}
                onSelect={() => {
                  forceAnnouncementToScreenReaders(`Copied lines ${rowBeginNumber}-${rowEndNumber}.`)
                  let text = ''
                  for (let i = rowBeginNumber; i <= rowEndNumber; i++) {
                    text += `${calculateTextContentFromElement(ssrSafeDocument?.getElementById(`LC${i}`) ?? null)}${
                      i !== rowEndNumber ? '\n' : ''
                    }`
                  }
                  if (text) copyText(text)
                  setShouldBeOpen(false)
                }}
                className={firstOptionId}
              >
                Copy lines
              </ActionList.Item>
            )}
            {permalinkUrl && (
              <ActionList.Item
                onClick={() => {
                  forceAnnouncementToScreenReaders(`Copied permalink.`)
                  copyText(permalinkUrl)
                  setShouldBeOpen(false)
                }}
                onSelect={() => {
                  forceAnnouncementToScreenReaders(`Copied permalink.`)
                  copyText(permalinkUrl)
                  setShouldBeOpen(false)
                }}
              >
                Copy permalink
              </ActionList.Item>
            )}
            <ActionList.LinkItem href={getUrl({action: 'blame', hash: linkHash})}>View git blame</ActionList.LinkItem>
            {newIssuePath && permalinkUrl && (
              <ActionList.LinkItem href={`${newIssuePath}?permalink=${encodedPermalinkUrl}`}>
                Reference in new issue
              </ActionList.LinkItem>
            )}
            {newDiscussionPath && permalinkUrl && (
              <ActionList.LinkItem href={`${newDiscussionPath}?permalink=${encodedPermalinkUrl}`}>
                Reference in new discussion
              </ActionList.LinkItem>
            )}
            {githubDevUrl && (
              <ActionList.LinkItem href={githubDevUrl + ssrSafeWindow?.location.pathname.substring(1)}>
                View file in GitHub.dev
              </ActionList.LinkItem>
            )}
            {rowBeginNumber === rowEndNumber && lineData && (
              <ActionList.Item onClick={expandOrCollapseSection} onSelect={expandOrCollapseSection}>
                {lineData.ownedSection && ownedSectionCollapsed ? 'Expand' : 'Collapse'} current section
              </ActionList.Item>
            )}
            <ActionList.Item onClick={openUpRefSelector} onSelect={openUpRefSelector}>
              View file in different branch/tag
              <ActionList.TrailingVisual>
                <CommandKeybindingHint commandId="code-view:open-ref-selector" />
              </ActionList.TrailingVisual>
            </ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
    )

  const portalRoot = ssrSafeDocument?.getElementById(portalRootID)

  return portalRoot ? createPortal(element, portalRoot) : null
})

export default HighlightedLineMenu
